export const STATUS_OPTIONS = [
  { value: 'running', label: 'Running' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'draft', label: 'Draft' },
];

export const STATUS_SECTIONS = [
  { value: 'running', label: 'Running' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
  { value: 'completed', label: 'Completed' },
];

const DECISION_LABELS = {
  srm: 'SRM',
  collecting: 'Collecting',
  guardrail_fail: 'Guardrail fail',
  ship_candidate: 'Ship candidate',
  losing: 'Losing',
  inconclusive: 'Inconclusive',
};

const DECISION_MEANING = {
  srm: 'Traffic split is off. Do not read the lift.',
  collecting: 'An arm is still short of the planned 3,841 exposures.',
  guardrail_fail: 'The guardrail interval sits entirely below the allowed drop.',
  ship_candidate: 'The lift interval is entirely above zero. Advisory only.',
  losing: 'The lift interval is entirely below zero. Advisory only.',
  inconclusive: 'The sample is in and the lift interval still crosses zero.',
};

const CALL_DECISIONS = new Set(['srm', 'guardrail_fail', 'ship_candidate', 'losing', 'inconclusive']);

export const ADVISORY_COPY =
  'Not a ship button. Pause stops new counted events on the API even before the client ships, and on the client after the config fetch. Counts are still forgeable under the collect IP limiter.';

export const NOT_READY_COPY =
  'The interval is not a ship signal until every arm reaches the planned sample.';

export const MUTEX_COPY =
  'Mutex conflict: both funnel experiments are effectively running. Config reports both paused; ingest drops both.';

export const MOUNTED_NOTE =
  'This key is assigned on the public website.';

export const UNMOUNTED_NOTE =
  'Registered only. The website does not assign this key yet, so a running status will not collect exposures.';

export function rateLabel(rate) {
  if (rate == null || !Number.isFinite(Number(rate))) return '—';
  return `${(Number(rate) * 100).toFixed(1)}%`;
}

export function intervalLabel(low, high) {
  if (low == null || high == null) return '—';
  return `${rateLabel(low)}–${rateLabel(high)}`;
}

function signedPoints(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const points = Number(value) * 100;
  const sign = points > 0 ? '+' : '';
  return `${sign}${points.toFixed(1)}pp`;
}

export function liftLabel(lift, low, high) {
  if (lift == null || !Number.isFinite(Number(lift))) return '—';
  if (low == null || high == null) return signedPoints(lift);
  return `${signedPoints(lift)} (${signedPoints(low)} to ${signedPoints(high)})`;
}

export function decisionLabel(decision) {
  return DECISION_LABELS[decision] || decision || '—';
}

export function decisionMeaning(decision) {
  return DECISION_MEANING[decision] || 'No decision yet.';
}

export function srmLabel({ srmFlagged, srmChecked } = {}) {
  if (srmFlagged) return 'SRM flagged';
  if (srmChecked) return 'SRM checked';
  return 'SRM not checked';
}

export function needsCall(experiment) {
  return Boolean(experiment?.mutexConflict) || CALL_DECISIONS.has(experiment?.decision);
}

export function pageMounted(experiment) {
  if (typeof experiment?.pageMounted === 'boolean') return experiment.pageMounted;
  return experiment?.key === 'hp_hero_cta' || experiment?.key === 'pricing_plan_cta';
}

export function armProgress(experiment) {
  const planned = Number(experiment?.plannedExposuresPerArm) || 0;
  const arms = (experiment?.variants || []).map((variant) => {
    const exposures = Number(variant.exposures) || 0;
    return {
      key: variant.variantKey,
      label: variant.label,
      exposures,
      remaining: Math.max(0, planned - exposures),
      ratio: planned > 0 ? Math.min(1, exposures / planned) : 0,
    };
  });
  const slowest = arms.reduce(
    (min, arm) => (arm.exposures < min.exposures ? arm : min),
    arms[0] || { key: '', exposures: 0, remaining: planned, ratio: 0 },
  );
  return { planned, arms, slowest };
}

export function treatmentLift(experiment) {
  return (experiment?.variants || []).find((variant) => variant.lift != null) || null;
}

export function summarizeExperiments(experiments = []) {
  const counts = { running: 0, paused: 0, draft: 0, completed: 0 };
  let exposures = 0;
  let conversions = 0;
  let mutexConflict = false;
  let needsCallCount = 0;
  let shipCandidates = 0;
  for (const experiment of experiments) {
    if (Object.prototype.hasOwnProperty.call(counts, experiment.status)) {
      counts[experiment.status] += 1;
    }
    exposures += Number(experiment.exposures) || 0;
    conversions += Number(experiment.conversions) || 0;
    if (experiment.mutexConflict) mutexConflict = true;
    if (needsCall(experiment)) needsCallCount += 1;
    if (experiment.decision === 'ship_candidate') shipCandidates += 1;
  }
  return {
    counts,
    exposures,
    conversions,
    mutexConflict,
    total: experiments.length,
    needsCall: needsCallCount,
    shipCandidates,
  };
}

export function experimentHaystack(experiment) {
  return [
    experiment.name,
    experiment.key,
    experiment.pagePath,
    experiment.status,
    decisionLabel(experiment.decision),
    experiment.primaryGoal,
    experiment.primaryGoalLabel,
  ].join(' ').toLowerCase();
}

export function filterExperiments(experiments, { query = '', filter = 'all' } = {}) {
  const needle = query.trim().toLowerCase();
  return experiments.filter((experiment) => {
    if (filter === 'call' && !needsCall(experiment)) return false;
    if (filter !== 'all' && filter !== 'call' && experiment.status !== filter) return false;
    if (needle && !experimentHaystack(experiment).includes(needle)) return false;
    return true;
  });
}

export function mutationMessage(error) {
  return error?.graphQLErrors?.[0]?.message || error?.message || 'Save failed';
}

export function publicSiteOrigin(
  env = process.env.REACT_APP_ENV,
  explicit = process.env.REACT_APP_PUBLIC_SITE_URL,
) {
  if (explicit) return String(explicit).replace(/\/$/, '');
  if (env === 'production') return 'https://fiscalflowcast.com';
  if (env === 'staging') return 'https://staging.fiscalflowcast.com';
  return 'http://127.0.0.1:3005';
}

export function experimentPreviewUrl(experiment, variantKey, origin = publicSiteOrigin()) {
  const path = experiment?.pagePath && experiment.pagePath.startsWith('/')
    ? experiment.pagePath
    : '/';
  const url = new URL(path, `${String(origin).replace(/\/$/, '')}/`);
  url.searchParams.set('ffc_exp', `${experiment.key}:${variantKey}`);
  return url.toString();
}
